// /admin/whatsapp-connect: connect Fleetly's own WhatsApp Business app number
// to the Cloud API with Meta's Embedded Signup, keeping the app on the phone
// ("coexistence"). Done once, by the number's owner.
//
// SHIPS SWITCHED OFF. Until whatsapp_connect_settings.enabled is true and the
// Embedded Signup configuration id is entered (both after Meta App Review),
// the page says so and loads nothing from Meta.
//
// THE FACEBOOK SDK LOADS ON THIS PAGE ONLY, and only once the page is ready to
// connect. It loads on arrival rather than on the click, because FB.login
// opens a popup and a popup opened after an await is no longer a user gesture,
// so the browser blocks it.
//
// SECRETS NEVER REACH THE BROWSER. The page holds Meta's app id and the
// configuration id (Meta's own sample puts both in page source) and, for at
// most 30 seconds, the exchangeable code. The code goes to the Worker at once;
// the app secret and the business token live in the Supabase vault.
//
// Every sentence about state comes from wa_connect_status_v1 in Postgres,
// including Meta's 24 hour sync deadline. CLAUDE.md rule 4.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { CheckCircle2, CircleAlert, Loader2, PlugZap, PowerOff, RefreshCw, ShieldAlert } from 'lucide-react'
import { useGetConnectStatus, useSubmitConnect } from '../hooks/backend/whatsapp'
import { Button } from '../lib/shadcn/button'
import { Checkbox } from '../lib/shadcn/checkbox'
import { Spinner } from '../lib/shadcn/spinner'
import {
  CHECKLIST,
  FB_SDK_GRAPH_VERSION,
  FB_SDK_URL,
  loginOptions,
  pageState,
  parseSignupMessage,
  type ConnectStatus,
  type SignupMessage,
} from '../utils/whatsappConnect'

type FbAuthResponse = { authResponse?: { code?: string } | null; status?: string }
type FbSdk = {
  init: (o: Record<string, unknown>) => void
  login: (cb: (r: FbAuthResponse) => void, o: Record<string, unknown>) => void
}
declare global {
  interface Window {
    FB?: FbSdk
    fbAsyncInit?: () => void
  }
}

const SDK_ID = 'facebook-jssdk'

/** Loads and initialises Meta's SDK once. Resolves when FB.init has run. */
function loadFacebookSdk(appId: string): Promise<FbSdk> {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB)
      return
    }
    window.fbAsyncInit = () => {
      if (!window.FB) return reject(new Error('The Facebook sign in script loaded but did not start.'))
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version: FB_SDK_GRAPH_VERSION })
      resolve(window.FB)
    }
    if (document.getElementById(SDK_ID)) return
    const s = document.createElement('script')
    s.id = SDK_ID
    s.async = true
    s.defer = true
    s.crossOrigin = 'anonymous'
    s.src = FB_SDK_URL
    s.onerror = () =>
      reject(new Error('The Facebook sign in script could not load. A browser extension that blocks Facebook will stop it; allow connect.facebook.net on this page.'))
    document.body.appendChild(s)
  })
}

const SAST = new Intl.DateTimeFormat('en-ZA', {
  timeZone: 'Africa/Johannesburg',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})
const when = (iso: string | null | undefined) => (iso ? SAST.format(new Date(iso)) + ' SAST' : '—')

// How long to wait for Meta's session message after the code arrives. The two
// come from different channels in no fixed order, and the code dies at 30 s.
const SESSION_WAIT_MS = 4000

export default function WhatsAppConnect() {
  const statusQuery = useGetConnectStatus<ConnectStatus>()
  const submit = useSubmitConnect<{ outcome: string }>()
  const loadStatus = statusQuery.trigger
  const refresh = useCallback(() => {
    void loadStatus({}, { skipCache: true }).catch(() => undefined)
  }, [loadStatus])
  useEffect(refresh, [refresh])

  const status = statusQuery.data
  const state = status ? pageState(status) : null

  const [ticked, setTicked] = useState<Record<string, boolean>>({})
  const allTicked = CHECKLIST.every((c) => ticked[c.id])
  const [sdk, setSdk] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle')
  const [sdkError, setSdkError] = useState<string | null>(null)
  const [busy, setBusy] = useState<null | 'popup' | 'connecting' | 'resuming'>(null)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null)
  const sessionRef = useRef<SignupMessage | null>(null)

  const canLaunch = state === 'ready' || state === 'failed'
  const appId = status?.meta_app_id ?? null

  useEffect(() => {
    if (!canLaunch || !appId || sdk !== 'idle') return
    setSdk('loading')
    loadFacebookSdk(appId)
      .then(() => setSdk('ready'))
      .catch((e: unknown) => {
        setSdk('failed')
        setSdkError(e instanceof Error ? e.message : String(e))
      })
  }, [canLaunch, appId, sdk])

  // Meta's session logging message: the account ids on finish, the screen
  // that was open on cancel, or an error Meta showed inside the window.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const m = parseSignupMessage(event.origin, event.data)
      if (m) sessionRef.current = m
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const waitForSession = async (): Promise<SignupMessage | null> => {
    const until = Date.now() + SESSION_WAIT_MS
    while (!sessionRef.current && Date.now() < until) {
      await new Promise((r) => window.setTimeout(r, 200))
    }
    return sessionRef.current
  }

  const afterCode = async (code: string) => {
    setBusy('connecting')
    const s = await waitForSession()
    const fin = s && s.kind === 'finish' ? s : null
    try {
      await submit.trigger({
        code,
        waba_id: fin?.waba_id ?? null,
        phone_number_id: fin?.phone_number_id ?? null,
        business_id: fin?.business_id ?? null,
        flow_event: fin?.event ?? null,
      })
      setNotice({
        tone: 'ok',
        text: 'Connected. Meta is now syncing contacts and, if you chose to share it, chat history. Keep the WhatsApp Business app open on the phone for the next few minutes.',
      })
    } catch (e) {
      setNotice({ tone: 'bad', text: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(null)
      refresh()
    }
  }

  const afterNoCode = async () => {
    const s = await waitForSession()
    const outcome = s?.kind === 'error' ? 'failed' : 'cancelled'
    const reason =
      s?.kind === 'error'
        ? `${s.error_message}${s.error_code ? ` (code ${s.error_code}` : ''}${s.session_id ? `, session ${s.session_id}` : ''}${s.error_code ? ')' : ''}`
        : s?.kind === 'cancel' && s.current_step
          ? `The signup window was closed at ${s.current_step}.`
          : 'The signup window was closed before it finished.'
    setNotice({ tone: 'bad', text: `${reason} Nothing was connected.` })
    try {
      await submit.trigger({ unfinished: outcome, flow_event: s?.kind === 'cancel' ? 'CANCEL' : s?.kind === 'error' ? 'ERROR' : null, error: reason })
    } catch {
      // The notice already says what happened; failing to record it is not worth a second message.
    } finally {
      setBusy(null)
      refresh()
    }
  }

  const launch = () => {
    if (!window.FB || !status?.es_config_id) return
    setNotice(null)
    sessionRef.current = null
    setBusy('popup')
    // The SDK wants a plain function here, not an async one.
    window.FB.login((response) => {
      const code = response?.authResponse?.code
      if (code) void afterCode(code)
      else void afterNoCode()
    }, loginOptions(status.es_config_id))
  }

  const resume = async () => {
    setNotice(null)
    setBusy('resuming')
    try {
      await submit.trigger({ resume: true })
      setNotice({ tone: 'ok', text: 'The remaining steps finished. The number is connected.' })
    } catch (e) {
      setNotice({ tone: 'bad', text: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(null)
      refresh()
    }
  }

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <PlugZap className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-foreground">Connect WhatsApp</h1>
            <p className="text-sm text-muted-foreground">
              Fleetly’s WhatsApp Business app number, connected to the cockpit while it stays on the phone.
            </p>
          </div>
        </div>

        {!status && !statusQuery.error && (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Checking access…
          </div>
        )}

        {statusQuery.error && !status && (
          <div className="space-y-3 rounded-lg border border-border bg-card p-6 text-sm">
            <p className="text-rose-700 dark:text-rose-300">{statusQuery.error}</p>
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Try again
            </Button>
          </div>
        )}

        {state === 'not_permitted' && (
          <StateCard icon={<ShieldAlert className="h-6 w-6" />} title="This page is not open to your login.">
            Connecting Fleetly’s WhatsApp number is for the person who owns it. If that should be you, ask an admin to
            switch on can_connect_whatsapp for you in cockpit access.
          </StateCard>
        )}

        {state === 'off' && (
          <StateCard icon={<PowerOff className="h-6 w-6" />} title="Connecting WhatsApp is switched off.">
            It stays off until Meta approves the Fleetly app and the Embedded Signup configuration is created. Nothing on
            this page talks to Meta until then, and the number on the phone is unchanged.
          </StateCard>
        )}

        {state === 'no_config' && (
          <StateCard icon={<CircleAlert className="h-6 w-6" />} title="Switched on, but not set up yet.">
            The Embedded Signup configuration id {status?.meta_app_id ? '' : 'and the Meta app id '}has not been entered.
            Create the configuration in the Meta app dashboard (Facebook Login for Business, Configurations), then enter
            its id in whatsapp_connect_settings. Nothing has been sent to Meta.
          </StateCard>
        )}

        {status?.connected && (state === 'connected' || state === 'failed') && (
          <div className="rounded-lg border border-emerald-200 bg-card p-5 dark:border-emerald-900">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div className="min-w-0 flex-1 space-y-3">
                <p className="text-sm font-semibold text-foreground">
                  Connected{status.connected.display_phone_number ? `: ${status.connected.display_phone_number}` : ''}.
                </p>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                  <Fact label="WhatsApp Business Account id" value={status.connected.waba_id} />
                  <Fact label="Phone number id" value={status.connected.phone_number_id} />
                  <Fact label="Connected" value={when(status.connected.connected_at)} />
                  <Fact
                    label="Contact and history sync"
                    value={
                      status.connected.steps?.sync_contacts?.ok && status.connected.steps?.sync_history?.ok
                        ? 'Both requested from Meta'
                        : 'Not both requested'
                    }
                  />
                </dl>
                <p className="text-xs text-muted-foreground">
                  The inbox still sends from the number in its own settings. Moving the inbox onto this number is a
                  separate step.
                </p>
              </div>
            </div>
          </div>
        )}

        {state === 'failed' && status?.last_attempt && (
          <div className="rounded-lg border border-rose-200 bg-card p-5 dark:border-rose-900">
            <div className="flex items-start gap-3">
              <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
              <div className="min-w-0 flex-1 space-y-2 text-sm">
                <p className="font-semibold text-foreground">The last connection did not finish.</p>
                <p className="break-words text-rose-800 dark:text-rose-200">
                  {status.last_attempt.error_text ?? 'No reason was recorded.'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Started by {status.last_attempt.by}, {when(status.last_attempt.started_at)}.
                </p>
                {status.resumable && (
                  <div className="space-y-2 pt-1">
                    <p className="text-xs text-muted-foreground">
                      The number is connected but not every step after it ran. Meta allows until{' '}
                      {when(status.last_attempt.sync_deadline)} to finish them.
                    </p>
                    <Button variant="brand" size="sm" onClick={() => void resume()} disabled={busy !== null}>
                      {busy === 'resuming' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                      Finish connecting
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {notice && (
          <div
            className={
              notice.tone === 'ok'
                ? 'rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100'
                : 'rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-100'
            }
          >
            {notice.text}
          </div>
        )}

        {canLaunch && !status?.connected && (
          <div className="space-y-4 rounded-lg border border-border bg-card p-5">
            <div className="space-y-1">
              <h2 className="text-base font-semibold text-foreground">Before you connect</h2>
              <p className="text-sm text-muted-foreground">
                Have the phone with the WhatsApp Business app in your hand. Meta sends it a message to confirm. Tick each
                line once it is true.
              </p>
            </div>
            <ul className="space-y-3">
              {CHECKLIST.map((c) => (
                <li key={c.id}>
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-foreground">
                    <Checkbox
                      className="mt-0.5 border-[hsl(var(--brand))] data-[state=checked]:bg-[hsl(var(--brand))] data-[state=checked]:text-white"
                      checked={ticked[c.id] === true}
                      onCheckedChange={(v) => setTicked((t) => ({ ...t, [c.id]: v === true }))}
                      disabled={busy !== null}
                    />
                    <span>{c.text}</span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-2 border-t border-border pt-4 sm:flex-row sm:items-center">
              <Button
                variant="brand"
                onClick={launch}
                disabled={!allTicked || sdk !== 'ready' || busy !== null}
                className="w-full sm:w-auto"
              >
                {busy === 'popup' || busy === 'connecting' ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <PlugZap className="mr-1.5 h-4 w-4" />
                )}
                {busy === 'connecting' ? 'Connecting…' : 'Connect with Facebook'}
              </Button>
              <p className="text-xs text-muted-foreground">
                {busy === 'connecting'
                  ? 'Exchanging the code and starting the sync. Keep the WhatsApp Business app open on the phone.'
                  : busy === 'popup'
                    ? 'Finish the steps in the Facebook window.'
                    : sdk === 'loading'
                      ? 'Loading Facebook sign in…'
                      : sdk === 'failed'
                        ? sdkError
                        : !allTicked
                          ? `Tick all ${CHECKLIST.length} lines to continue.`
                          : 'Opens Meta’s signup in a new window. Choose to connect your existing WhatsApp Business app.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function StateCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
        {icon}
      </div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="max-w-md text-sm text-muted-foreground">{children}</p>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-all font-medium tabular-nums text-foreground">{value ?? '—'}</dd>
    </div>
  )
}
